/**
 * Profile Caching Utilities
 * 
 * SECURITY: Provides localStorage-based caching for user profiles
 * with MINIMIZED data to reduce PII exposure risk.
 * 
 * Only non-sensitive identifiers are cached:
 * - user_id (required for profile lookup)
 * - organization_id (required for org context)
 * - has_organization flag
 * 
 * PII like name, email, phone, address are NOT cached.
 * 
 * @see security finding: localstorage_pii
 */

import { UserProfile } from '@/types/auth';

const CACHE_KEY = 'profile_cache_v2'; // Version bump for new schema
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * SECURITY: Minimal cache structure - no PII
 * Only stores non-sensitive identifiers needed for app context
 */
interface MinimalCachedProfile {
  user_id: string;
  organization_id: string | null;
  has_organization: boolean;
  timestamp: number;
}

/**
 * Cache minimal profile identifiers (no PII)
 * 
 * SECURITY: Only caches user_id and organization_id.
 * Display names and other PII come from session metadata.
 */
export function cacheProfile(profile: UserProfile): void {
  if (!profile?.user_id) {
    console.warn('[ProfileCache] Cannot cache profile without user_id');
    return;
  }

  try {
    // SECURITY: Only cache non-sensitive identifiers
    const cached: MinimalCachedProfile = {
      user_id: profile.user_id,
      organization_id: profile.organization_id ?? null,
      has_organization: !!profile.organization_id,
      timestamp: Date.now(),
    };
    
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
    console.debug('[ProfileCache] Minimal profile cached');
  } catch (error) {
    // Handle quota exceeded or other localStorage errors
    console.warn('[ProfileCache] Failed to cache profile');
  }
}

/**
 * SECURITY: Minimal cached data for context checks only
 * Does NOT return a full UserProfile - use RPC for that
 */
export interface CachedProfileContext {
  user_id: string;
  organization_id: string | null;
  has_organization: boolean;
}

/**
 * Retrieve cached profile context for a specific user
 * 
 * SECURITY: Returns only non-PII identifiers, not a full profile
 */
export function getCachedContext(userId: string): CachedProfileContext | null {
  try {
    const item = localStorage.getItem(CACHE_KEY);
    if (!item) {
      return null;
    }

    const cached: MinimalCachedProfile = JSON.parse(item);
    
    // Verify it's for the correct user
    if (cached.user_id !== userId) {
      console.debug('[ProfileCache] Cached data is for different user');
      return null;
    }

    return {
      user_id: cached.user_id,
      organization_id: cached.organization_id,
      has_organization: cached.has_organization,
    };
  } catch {
    return null;
  }
}

/**
 * @deprecated Use getCachedContext instead - returns minimal data
 * Kept for backward compatibility during transition
 */
export function getProfileFromCache(userId: string): UserProfile | null {
  // SECURITY: No longer caches full profiles
  // Return null to force RPC fetch
  console.debug('[ProfileCache] Full profile cache deprecated, use RPC');
  return null;
}

/**
 * Check if cached profile context is still valid (within TTL)
 */
export function isCacheValid(userId: string): boolean {
  try {
    const item = localStorage.getItem(CACHE_KEY);
    if (!item) {
      return false;
    }

    const cached: MinimalCachedProfile = JSON.parse(item);
    
    // Check user ID match
    if (cached.user_id !== userId) {
      return false;
    }

    // Check TTL
    const age = Date.now() - cached.timestamp;
    return age < CACHE_TTL;
  } catch {
    return false;
  }
}

/**
 * @deprecated Use getCachedContext with isCacheValid instead
 */
export function getValidCachedProfile(userId: string): UserProfile | null {
  // SECURITY: No longer returns full profiles from cache
  return null;
}

/**
 * Clear cached profile context
 */
export function clearProfileCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
    // Also clear old v1 cache if present
    localStorage.removeItem('profile_cache_v1');
    console.debug('[ProfileCache] Cache cleared');
  } catch {
    console.warn('[ProfileCache] Failed to clear cache');
  }
}

/**
 * Get cache age in seconds
 */
export function getCacheAge(userId: string): number | null {
  try {
    const item = localStorage.getItem(CACHE_KEY);
    if (!item) {
      return null;
    }

    const cached: MinimalCachedProfile = JSON.parse(item);
    
    if (cached.user_id !== userId) {
      return null;
    }

    return Math.floor((Date.now() - cached.timestamp) / 1000);
  } catch {
    return null;
  }
}
